<script>
  import Header from "../components/Header.svelte";
  import UpdatePostModal from "../components/UpdatePostModal.svelte";
  import DeleteModal from "../components/DeleteModal.svelte";
  import { onMount, onDestroy } from "svelte";
  import ProtectedRoute from "../components/ProtectedRoute.svelte";
  import LandingHeader from "../components/LandingHeader.svelte";

  let userData = null;
  let videos = [];
  const maxPostsToShow = 12;
  let skip = 0;
  let loading = false;
  let activeVideoId = null;
  let showUpdateModal = false;
  let showDeleteModal = false;
  let selectedVideo = null;
  let videoToDelete = null;
  let hasMorePosts = true; // Flag to track if there are more posts

  onMount(() => {
    userData = JSON.parse(localStorage.getItem("userData"));
    loadSubscribedVideos(); // Initial load
    window.addEventListener("scroll", handleScroll);
  });

  onDestroy(() => {
    window.removeEventListener("scroll", handleScroll);
  });

  let lastSkip = null;

  async function loadSubscribedVideos() {
    if (loading || !hasMorePosts) return;
    loading = true;

    try {
      const token = localStorage.getItem("token");

      // Fetch both site-subscription-posts and site-subscription-posts-alias
      const [postsResponse, aliasResponse] = await Promise.all([
        fetch(
          `https://api.trulens.ae/api/subscribe/site-subscription-posts?limit=${maxPostsToShow}&skip=${skip}&_=${Date.now()}`,
          {
            headers: {
              Authorization: userData.token,
            },
          }
        ),
        fetch(
          `https://api.trulens.ae/api/subscribe/site-subscription-posts-alias?limit=${maxPostsToShow}&skip=${skip}&_=${Date.now()}`,
          {
            headers: {
              Authorization: userData.token,
            },
          }
        ),
      ]);

      // Handle responses
      const postsData = postsResponse.ok ? await postsResponse.json() : [];
      const aliasData = aliasResponse.ok ? await aliasResponse.json() : [];

      const combinedData = [...postsData, ...aliasData];

      if (combinedData.length < maxPostsToShow) {
        hasMorePosts = false;
      }

      // Append combined data to videos array
      videos = [...videos, ...combinedData];

      // Fetch mime types for new videos
      // for (let video of combinedData) {
      //   if (!video.mimeType) {
      //     const mimeResponse = await fetch(
      //       `https://api.trulens.ae/api/videos/mime/${video._id}?_=${Date.now()}`,
      //       {
      //         headers: {
      //           Authorization: userData.token,
      //         },
      //       }
      //     );
      //     if (mimeResponse.ok) {
      //       const mimeType = await mimeResponse.text();
      //       video.mimeType = mimeType;
      //     }
      //   }
      // }

      skip += maxPostsToShow; // Increment skip for pagination
    } catch (error) {
      // console.error("Failed to load subscribed videos:", error);
    } finally {
      loading = false;
    }
  }

  async function handleScroll() {
    if (
      window.innerHeight + window.scrollY >= document.body.offsetHeight &&
      !loading &&
      hasMorePosts // Ensures no more posts are loaded if hasMorePosts is false
    ) {
      await loadSubscribedVideos();
    }
  }

  function handleVideoClick(video) {
    if (activeVideoId === video._id) {
      activeVideoId = null;
    } else {
      window.location.href = `#/video/${video._id}`;
    }
  }

  function handleOptionsClick(videoId) {
    activeVideoId = videoId;
  }

  function closeOptions(event) {
    if (event) {
      event.stopPropagation();
    }
    activeVideoId = null;
  }

  function editPost(event, video) {
    event.stopPropagation();
    selectedVideo = video;
    showUpdateModal = true;
    closeOptions(event);
  }

  function handleUpdate(event) {
    videos = videos.map((v) => (v._id === event.detail._id ? event.detail : v));
  }

  function confirmDelete(event, video) {
    event.stopPropagation();
    videoToDelete = video;
    showDeleteModal = true;
    closeOptions(event);
  }

  async function deletePost(event) {
    event.stopPropagation();
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `https://api.trulens.ae/api/videos/delete/${videoToDelete._id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: userData.token,
          },
        }
      );

      if (response.ok) {
        videos = videos.filter((video) => video._id !== videoToDelete._id);
        // console.log("Post deleted successfully");
      } else {
        const errorData = await response.json();
        // console.error("Failed to delete post:", errorData.error);
      }
    } catch (error) {
      // console.error("Error deleting post:", error);
    }
    showDeleteModal = false;
    videoToDelete = null;
  }

  function nameTimestamp(timestamp) {
    const cleanTimestamp = timestamp
      .replace(/\.(mkv|jpg)/, "")
      .replace(/_/g, ":");
    return formatTimestamp(cleanTimestamp);
  }

  function formatTimestamp(timestamp) {
    const date = new Date(timestamp);

    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const dayName = days[date.getDay()];
    const day = date.getDate();
    const ordinalSuffix = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    const monthName = months[date.getMonth()];
    const year = String(date.getFullYear()).slice(-2);

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${dayName}, ${ordinalSuffix(day)} ${monthName} ${year} ${hours}:${minutes} ${ampm}`;
  }

  function handleOutsideClick(event) {
    const optionsMenu = document.querySelector(".options-menu");
    if (optionsMenu && !optionsMenu.contains(event.target)) {
      closeOptions();
    }
  }

  onMount(() => {
    document.addEventListener("click", handleOutsideClick);
  });

  onDestroy(() => {
    document.removeEventListener("click", handleOutsideClick);
  });
</script>

<link
  href="https://fonts.googleapis.com/icon?family=Material+Icons"
  rel="stylesheet"
/>
<link
  rel="stylesheet"
  href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css"
/>

<ProtectedRoute>
  <div class="content">
    <LandingHeader />
    <div class="title">
      <span class="title-span">Subscriptions</span>
    </div>

    {#if videos.length === 0 && !loading}
      <div class="no-videos">There are no subscription posts.</div>
    {/if}

    <div class="video-container">
      {#each videos as video}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div class="video-wrapper" on:click={() => handleVideoClick(video)}>
          <div class="video-card" on:click={() => handleVideoClick(video)}>
            {#if video.mimeType.startsWith("video/")}
              <!-- svelte-ignore a11y-media-has-caption -->
              <video src={video.downloadURL} class={video.rotateClass} controls />
            {:else if video.mimeType.startsWith("image/")}
              <img
                src={video.downloadURL}
                alt={video.title}
                class={video.rotateClass}
              />
            {/if}
          </div>
          <div class="bottom-content">
            <div class="user-info">
              {#if video.isNamePrivate}
                <!-- Profile Picture and User Name -->
                <div class="profile-info">
                  <img
                    class="profile-picture"
                    src="/anonymous.jpg"
                    alt="Profile Picture"
                  />
                  <span style="font-weight: bold;">{video.alternateName}</span>
                  &nbsp;
                  <i class="fa-solid fa-user-lock" style="font-size: small;"></i>
                </div>
              {:else}
                <!-- Profile Picture and User Name -->
                <div class="profile-info">
                  <img
                    class="profile-picture"
                    src={video.userId.profilePicture || "/profile.png"}
                    alt="Profile Picture"
                  />
                  <span style="font-weight: bold;">{video.userId.name}</span>
                </div>
              {/if}
            </div>
            <div class="video-title">{video.title}</div>
            <div class="time-info">
              <span>{nameTimestamp(video.originalName)}</span>
            </div>
          </div>
        </div>
      {/each}
      <br />
    </div>

    {#if loading}
      <div class="spinner"></div>
    {/if}

    {#if !hasMorePosts && videos.length > 0}
      <div class="no-more-posts">No more posts to load.</div>
    {/if}
  </div>
</ProtectedRoute>

<style>
  .profile-info {
    display: flex;
    align-items: center;
  }

  .profile-picture {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    margin-right: 10px;
  }

  .title {
    display: flex;
    justify-content: center;
    padding: 10px;
    box-shadow: 0 4px 8px rgba(135, 135, 135, 0.1);
  }

  .options-menu {
    position: absolute;
    bottom: 40px;
    left: 25px;
    background-color: white;
    border: 1px solid #ddd;
    border-radius: 5px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    z-index: 10;
  }

  .video-wrapper {
    position: relative; /* Ensure .options-menu is relative to this */
    width: 17%; /* Adjust width and margin as needed */
    margin: 10px;
    text-align: left;
  }

  .no-more-posts {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 5vh; /* Full viewport height */
    text-align: center;
    font-size: 1rem;
  }

  .title-span {
    margin-top: 5px;
    border-radius: 25%;
    padding: 5px;
    font-size: 1.4rem;
    font-weight: bold;
    color: #000000;
  }

  .content {
    position: relative;
    min-height: 100vh;
  }

  .video-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    padding: 20px; /* 50 */
    position: relative; /* Ensure relative positioning for absolute spinner */
  }

  /* .video-wrapper {
      width: 17%;
      margin: 10px;
      text-align: left;
    } */

  .video-card {
    position: relative;
    height: 350px;
    overflow: hidden;
    /* border-radius: 10px; */
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    cursor: pointer;
  }

  .video-card video,
  .video-card img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* border-radius: 10px; */
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  .bottom-content {
    padding: 10px;
    background: #f9f9f9;
    /* border-radius: 10px; */
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    cursor: pointer;
  }

  .video-title {
    /* font-weight: bold; */
    color: #000000;
  }

  .user-info {
    font-size: 0.9rem;
    color: #000000;
  }

  .tags-info {
    font-size: 0.6rem;
    color: #515151;
  }

  .time-info {
    font-size: 0.6rem;
    color: #515151;
  }

  .more-options {
    color: blue;
    cursor: pointer;
    margin-right: 20px;
    font-size: 1rem;
  }

  .options-menu {
    position: absolute;
    bottom: 40px;
    left: 25px;
    background-color: white;
    border: 1px solid #ddd;
    border-radius: 5px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    z-index: 10;
  }

  .options-menu > div {
    padding: 10px;
    cursor: pointer;
  }

  /* .options-menu > div:hover {
      background-color: #6c6c6c;
    } */

  .close-button {
    text-align: center;
    padding: 5px;
    cursor: pointer;
    background-color: #ddd;
    transition: background-color 0.3s ease;
    color: black;
  }

  .close-button:hover {
    background-color: #ff0000;
  }

  .delete-button {
    text-align: center;
    padding: 5px;
    cursor: pointer;
    background-color: #ddd;
    transition: background-color 0.3s ease;
    color: black;
  }

  .delete-button:hover {
    background-color: #ff3c82;
  }

  .edit-button {
    text-align: center;
    padding: 5px;
    cursor: pointer;
    background-color: #ddd;
    transition: background-color 0.3s ease;
    color: black;
  }

  .edit-button:hover {
    background-color: #b683d4;
  }

  .spinner {
    position: absolute;
    bottom: 10px;
    left: 48%;
    transform: translateX(-50%);
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 5px solid rgba(0, 0, 0, 0.1);
    border-top-color: #3498db;
    animation: spin 1s ease-in-out infinite;
  }

  .no-videos {
    text-align: center;
    font-size: 1rem;
    color: #000;
    margin-top: 150px;
    padding: 20px;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 767px) {
    .title {
      padding: 10px 20px;
    }

    .title-span {
      font-size: 1.2rem;
    }

    .video-container {
      padding: 20px;
    }

    .video-wrapper {
      width: 100%;
      margin: 10px 0;
      text-align: left;
    }

    .video-card {
      height: 65vh;
    }

    .bottom-content {
      padding: 10px;
      background: #f9f9f9;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .spinner {
      left: 46%;
      width: 20px;
      height: 20px;
    }
  }

  @media screen and (min-width: 768px) {
    .video-wrapper {
      width: 24.5%;
    }

    .video-card {
      height: 350px;
    }

    .title {
      margin-top: 40px;
    }
  }

  @media screen and (min-width: 850px) {
    .title {
      margin-top: 5px;
    }
  }

  @media screen and (min-width: 850px) and (min-height: 1280px) {
    .title {
      margin-top: 55px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1024px) and (max-height: 600px) {
    .video-wrapper {
      width: 20%; /* Three videos per row on large screens */
    }

    .video-card {
      height: 350px;
    }

    .title {
      margin-top: 5px;
    }
  }

  @media screen and (min-width: 1024px) and (min-height: 1300px) {
    .video-wrapper {
      width: 20%; /* Three videos per row on large screens */
    }

    .video-card {
      height: 350px;
    }

    .title {
      margin-top: 60px;
    }
  }

  @media screen and (min-width: 1440px) {
    .video-wrapper {
      width: 35%;
    }

    .video-card {
      height: 450px;
    }

    .title {
      margin-top: 20px;
    }
  }
</style>
